import {FC} from 'react';
import {Button, Stack} from '@mui/material';
import {AdminBatchJobType, AdminBatchSubmission, ExecutionMode} from '../../../types/adminBatch.ts';
import {supportsSubmissionAction} from '../batchJobTypeGroups.ts';

interface SubmissionActionsProps {
    submission: AdminBatchSubmission;
    jobId: number;
    jobType: AdminBatchJobType;
    onRecheck: (jobId: number, submissionId: number) => void;
    onCancel: (jobId: number, submissionId: number) => void;
    onNextProvider: (jobId: number, submissionId: number) => void;
    onRetryQuiz: (jobId: number, submissionId: number, executionMode: ExecutionMode) => void;
    onReprocessCached: (jobId: number, submissionId: number) => void;
    isPending: boolean;
}

export const SubmissionActions: FC<SubmissionActionsProps> = ({
    submission: s,
    jobId,
    jobType,
    onRecheck,
    onCancel,
    onNextProvider,
    onRetryQuiz,
    onReprocessCached,
    isPending,
}) => {
    return (
        <Stack direction="row" spacing={1} justifyContent="center">
            {/* 상태 재확인: COMPLETED 아닐 때 */}
            {s.status !== 'COMPLETED' && supportsSubmissionAction(jobType, 'RECHECK') && (
                <Button size="small" variant="outlined" disabled={isPending}
                    onClick={() => onRecheck(jobId, s.id)}>
                    상태 재확인
                </Button>
            )}

            {/* SUBMITTED 상태 전용 */}
            {s.status === 'SUBMITTED' && (
                <>
                    {supportsSubmissionAction(jobType, 'CANCEL') && (
                        <Button size="small" color="error" variant="outlined" disabled={isPending}
                            onClick={() => {
                                if (!confirm(`submission #${s.id} 를 중단할까요?`)) return;
                                onCancel(jobId, s.id);
                            }}>
                            중단
                        </Button>
                    )}
                    {supportsSubmissionAction(jobType, 'NEXT_PROVIDER') && (
                        <Button size="small" color="warning" variant="outlined" disabled={isPending}
                            onClick={() => {
                                if (!confirm(`submission #${s.id} 를 다음 provider로 재시도할까요?`)) return;
                                onNextProvider(jobId, s.id);
                            }}>
                            다음 AI
                        </Button>
                    )}
                </>
            )}

            {/* FAILED 상태 전용 */}
            {s.status === 'FAILED' && (
                <>
                    {supportsSubmissionAction(jobType, 'RETRY_QUIZ') && (
                        <Button size="small" variant="outlined" color="primary" disabled={isPending}
                            onClick={() => {
                                const mode = prompt('실행 모드:\n1: BATCH_API\n2: STANDARD (실시간)', '2');
                                if (!mode) return;
                                const executionMode: ExecutionMode = mode === '1' ? 'BATCH_API' : 'STANDARD';
                                if (!confirm(`submission #${s.id}을(를) ${executionMode} 모드로 재시도할까요?`)) return;
                                onRetryQuiz(jobId, s.id, executionMode);
                            }}>
                            재시도
                        </Button>
                    )}
                    {supportsSubmissionAction(jobType, 'REPROCESS_CACHED') && s.externalBatchId && (
                        <Button size="small" variant="outlined" color="secondary" disabled={isPending}
                            onClick={() => {
                                if (!confirm(`submission #${s.id}의 캐시된 AI 응답으로 재처리할까요?\n(AI API를 다시 호출하지 않습니다)`)) return;
                                onReprocessCached(jobId, s.id);
                            }}>
                            캐시 재처리
                        </Button>
                    )}
                </>
            )}
        </Stack>
    );
};
